import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { dashboardAPI, rechargeAPI, bannersAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import BannerSlider from '../components/BannerSlider';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Users, Wallet, TrendingUp, Smartphone, ArrowRight,
  CreditCard, Clock, CheckCircle, XCircle, Zap, Tv,
  IndianRupee, Activity, Target, ArrowUpRight,
  Car, Banknote, Flame, Wind, ChevronRight,
  Sparkles, CircleDot, Signal, BarChart3, Receipt,
  ArrowDownRight, RefreshCw,
} from 'lucide-react';

// ===== ADMIN / DISTRIBUTOR DASHBOARD =====
const AdminDashboard = ({ user }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async (silent = false) => {
      try {
        const res = await dashboardAPI.getAdminOverview();
        setOverview(res.data);
      } catch (e) { console.error('Admin overview error:', e); }
      finally { if (!silent) setLoading(false); }
    };
    fetchData();

    // Auto-refresh dashboard every 10 seconds
    const interval = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(interval);
  }, []);

  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

  const SectionTitle = ({ children }) => (
    <h2 className="text-lg font-bold text-slate-800 mb-3" data-testid={`section-${children.toLowerCase().replace(/\s+/g, '-')}`}>{children}</h2>
  );

  const StatCard = ({ label, amount, count, color = 'text-slate-800', highlight = false, href }) => {
    const displayAmount = Number(amount || 0);
    const displayCount = Number(count || 0);
    const inner = (
      <div className={`rounded-xl p-4 text-center transition-all cursor-pointer ${highlight ? 'bg-slate-100 border border-slate-200 hover:bg-slate-200' : 'bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-[#4680ff]/30'}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <p className={`text-sm font-semibold mb-2 ${color}`}>{label}</p>
        <p className="text-sm font-bold text-slate-900 font-mono">&#8377; {fmt(displayAmount)} | {displayCount}</p>
      </div>
    );
    return href ? <Link to={href}>{inner}</Link> : inner;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
      </div>
    );
  }

  const r = overview?.today_recharge || {};
  const u = overview?.users || {};
  const p = overview?.today_payment || {};
  const t = overview?.today_tickets || {};
  const ab = overview?.api_balance || {};

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome back, {user?.name}</h1>
          <p className="text-sm text-slate-500 mt-0.5 capitalize">{user?.role} Dashboard</p>
        </div>
        <Link to="/recharge">
          <Button className="bg-[#4680ff] hover:bg-[#2563eb] text-white rounded-lg h-10 px-5 text-sm font-semibold" data-testid="new-recharge-btn">
            <Zap className="w-4 h-4 mr-2" />New Recharge
          </Button>
        </Link>
      </div>

      {/* API Balances */}
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(ab.apis || []).map((api, i) => (
            <div key={api.id} className="rounded-xl p-5 bg-gradient-to-br from-[#4680ff] to-[#2563eb] text-white shadow-lg" data-testid={`api-balance-${api.api_code}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">{api.name}</p>
                <span className="text-[9px] bg-white/20 px-1.5 py-0.5 rounded font-mono">P{api.priority}</span>
              </div>
              <p className="text-3xl font-extrabold font-mono">&#8377; {fmt(api.balance || 0)}</p>
            </div>
          ))}
          <div className="rounded-xl p-5 bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-lg" data-testid="api-balance-used">
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-2">Today Used</p>
            <p className="text-3xl font-extrabold font-mono">&#8377; {fmt(ab.today_used || 0)}</p>
          </div>
        </div>
      </div>

      {/* Today Recharge */}
      <div>
        <SectionTitle>Today Recharge</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Success" amount={r.success?.amount} count={r.success?.count} color="text-emerald-600" href="/recharges?status=success" />
          <StatCard label="In Process" amount={r.in_process?.amount} count={r.in_process?.count} color="text-rose-500" href="/recharges?status=pending" />
          <StatCard label="Failure" amount={r.failure?.amount} count={r.failure?.count} color="text-rose-500" href="/recharges?status=failed" />
          <StatCard label="Refund" amount={r.refund?.amount} count={r.refund?.count} color="text-rose-500" href="/recharges?status=refund" />
          <StatCard label="Total" amount={r.total?.amount} count={r.total?.count} highlight href="/recharges" />
        </div>
      </div>

      {/* Users */}
      <div>
        <SectionTitle>Users</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="SDs" amount={u.sds?.balance} count={u.sds?.count} href="/super-distributors" />
          <StatCard label="Distributors" amount={u.distributors?.balance} count={u.distributors?.count} href="/users?role=distributor" />
          <StatCard label="Retailers" amount={u.retailers?.balance} count={u.retailers?.count} href="/retailers" />
          <StatCard label="Users" amount={u.users?.balance} count={u.users?.count} href="/users?role=user" />
          <StatCard label="API Users" amount={u.api_users?.balance} count={u.api_users?.count} href="/api-users" />
          <StatCard label="Total" amount={u.total?.balance} count={u.total?.count} highlight href="/users" />
        </div>
      </div>

      {/* Today Payment Request */}
      <div>
        <SectionTitle>Today Payment Request</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Accepted" amount={p.accepted?.amount} count={p.accepted?.count} color="text-emerald-600" href="/credit-debit" />
          <StatCard label="Pending" amount={p.pending?.amount} count={p.pending?.count} color="text-rose-500" href="/credit-debit" />
          <StatCard label="Rejected" amount={p.rejected?.amount} count={p.rejected?.count} color="text-rose-500" href="/credit-debit" />
          <StatCard label="Total" amount={p.total?.amount} count={p.total?.count} highlight href="/credit-debit" />
        </div>
      </div>

      {/* Complaints */}
      <div>
        <SectionTitle>Complaints</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" style={{ maxWidth: '650px' }}>
          <Link to="/complaints?status=open" className="rounded-xl p-4 text-center bg-white border border-red-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow" data-testid="complaint-open-card">
            <p className="text-sm font-semibold mb-2 text-red-600">Open</p>
            <p className="text-lg font-bold text-slate-900 font-mono">{t.open || 0}</p>
          </Link>
          <Link to="/complaints?status=in_progress" className="rounded-xl p-4 text-center bg-white border border-amber-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow" data-testid="complaint-in-progress-card">
            <p className="text-sm font-semibold mb-2 text-amber-600">In Progress</p>
            <p className="text-lg font-bold text-slate-900 font-mono">{t.in_progress || 0}</p>
          </Link>
          <Link to="/complaints?status=resolved" className="rounded-xl p-4 text-center bg-white border border-emerald-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow" data-testid="complaint-resolved-card">
            <p className="text-sm font-semibold mb-2 text-emerald-600">Resolved</p>
            <p className="text-lg font-bold text-slate-900 font-mono">{t.resolved || 0}</p>
          </Link>
          <Link to="/complaints" className="rounded-xl p-4 text-center bg-slate-100 border border-slate-200 hover:shadow-md transition-shadow" data-testid="complaint-total-card">
            <p className="text-sm font-semibold mb-2 text-slate-800">Total</p>
            <p className="text-lg font-bold text-slate-900 font-mono">{t.total || 0}</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ===== CIRCULAR PROGRESS RING =====
const ProgressRing = ({ progress, size = 80, strokeWidth = 6, color = '#f97316' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(progress, 100) / 100) * circumference;
  return (
    <svg width={size} height={size} className="rtl-ring-rotate">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
        className="rtl-ring-anim" style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }} />
    </svg>
  );
};

// ===== UNIQUE RETAILER DASHBOARD =====
const RetailerDashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, cRes, bRes] = await Promise.all([
          dashboardAPI.getRetailerStats(),
          dashboardAPI.getChartData(7),
          bannersAPI.list('dashboard').catch(() => ({ data: [] })),
        ]);
        setStats(sRes.data);
        setChartData(cRes.data);
        setBanners(bRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const greeting = now.getHours() < 12 ? 'Good Morning' : now.getHours() < 17 ? 'Good Afternoon' : 'Good Evening';
  const todayDate = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const walletBalance = stats?.wallet_balance || user?.wallet_balance || 0;
  const commRate = stats?.commission_rate || 0;
  const todayVol = stats?.today_volume || 0;
  const todayCount = stats?.today_count || 0;
  const todayComm = stats?.today_commission || 0;
  const successRate = stats?.success_rate || 0;
  const pendingCount = stats?.pending_recharges || 0;
  const totalTxns = stats?.total_recharges || 0;
  const totalEarned = stats?.total_commission || 0;
  const recentList = stats?.recent_recharges || [];

  // Daily target calculation (hypothetical target for UI)
  const dailyTarget = Math.max(todayVol, 5000);
  const targetProgress = dailyTarget > 0 ? Math.round((todayVol / dailyTarget) * 100) : 0;

  const statusConfig = {
    success: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', dot: 'bg-emerald-500', label: 'Success' },
    pending: { bg: 'bg-amber-500/10', text: 'text-amber-600', dot: 'bg-amber-500', label: 'Pending' },
    failed: { bg: 'bg-red-500/10', text: 'text-red-600', dot: 'bg-red-500', label: 'Failed' },
  };

  const quickServices = [
    { to: '/recharge', icon: Smartphone, label: 'Prepaid', color: '#f97316', bg: 'from-orange-500/10 to-orange-500/5', testId: 'qs-prepaid' },
    { to: '/recharge?type=postpaid', icon: Signal, label: 'Postpaid', color: '#4680ff', bg: 'from-blue-500/10 to-blue-500/5', testId: 'qs-postpaid' },
    { to: '/recharge?type=dth', icon: Tv, label: 'DTH', color: '#3b82f6', bg: 'from-blue-500/10 to-blue-500/5', testId: 'qs-dth' },
    { to: '/bill-payment?type=electricity', icon: Zap, label: 'Electricity', color: '#eab308', bg: 'from-yellow-500/10 to-yellow-500/5', testId: 'qs-electricity' },
    { to: '/bill-payment?type=fastag', icon: Car, label: 'FASTag', color: '#ef4444', bg: 'from-red-500/10 to-red-500/5', testId: 'qs-fastag' },
    { to: '/bill-payment?type=loan', icon: Banknote, label: 'Loan', color: '#14b8a6', bg: 'from-teal-500/10 to-teal-500/5', testId: 'qs-loan' },
    { to: '/bill-payment?type=gas', icon: Flame, label: 'Gas', color: '#f97316', bg: 'from-orange-500/10 to-orange-500/5', testId: 'qs-gas' },
    { to: '/services', icon: ChevronRight, label: 'All Services', color: '#8b5cf6', bg: 'from-violet-500/10 to-violet-500/5', testId: 'qs-all' },
  ];

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rtl-tooltip">
        <p className="text-[10px] text-white/50 mb-0.5">{label ? new Date(label).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}</p>
        <p className="text-sm font-bold text-white font-mono">&#8377;{payload[0].value?.toLocaleString('en-IN')}</p>
      </div>
    );
  };

  return (
    <div className="space-y-5 rtl-dashboard" data-testid="retailer-dashboard">

      {/* ====== HERO BANNER ====== */}
      <div className="rtl-hero rounded-2xl overflow-hidden relative animate-fade-in">
        {/* Mesh background */}
        <div className="absolute inset-0 rtl-mesh-bg" />
        <div className="absolute top-0 right-0 w-72 h-72 rtl-orb-1" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rtl-orb-2" />
        <div className="absolute top-1/2 left-1/2 w-40 h-40 rtl-orb-3" />

        <div className="relative z-10 p-5 md:p-7">
          {/* Top row: greeting + date */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-white/30 text-[11px] font-semibold uppercase tracking-[0.15em]">{greeting}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mt-1" data-testid="retailer-greeting">
                {user?.name}
              </h1>
              <p className="text-white/20 text-xs mt-1.5">{todayDate}</p>
            </div>
            <Badge className="rtl-comm-badge" data-testid="retailer-comm-badge">
              <Sparkles className="w-3 h-3 mr-1" />{commRate}% Commission
            </Badge>
          </div>

          {/* Balance + Target Ring */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
            {/* Balance block */}
            <div className="flex items-center gap-5">
              <div className="rtl-balance-card">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] font-bold text-white/30 uppercase tracking-wider">Wallet Balance</span>
                </div>
                <p className="text-3xl md:text-4xl font-bold text-white font-mono tracking-tighter" data-testid="retailer-balance">
                  &#8377;{walletBalance.toLocaleString('en-IN')}
                </p>
              </div>
            </div>

            {/* Daily Target Ring */}
            <div className="flex items-center gap-4" data-testid="daily-target-ring">
              <div className="relative">
                <ProgressRing progress={targetProgress} size={72} strokeWidth={5} color="#f97316" />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-sm font-bold text-white font-mono">{targetProgress}%</span>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-white/30 uppercase tracking-wider">Today's Target</p>
                <p className="text-sm font-bold text-white font-mono mt-0.5">&#8377;{todayVol.toLocaleString('en-IN')}</p>
                <p className="text-[10px] text-white/20">{todayCount} transactions</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== BANNER SLIDER ====== */}
      {banners.length > 0 && (
        <div className="animate-fade-in" style={{ animationDelay: '0.05s', opacity: 0 }}>
          <BannerSlider banners={banners} />
        </div>
      )}

      {/* ====== QUICK SERVICES ====== */}
      <div className="animate-fade-in" style={{ animationDelay: '0.08s', opacity: 0 }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-slate-700 tracking-tight">Quick Services</h2>
          <Link to="/services" className="text-xs text-[#4680ff] font-medium hover:text-[#2563eb] flex items-center gap-1">
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {quickServices.map(s => (
            <Link key={s.testId} to={s.to} data-testid={s.testId}>
              <div className={`rtl-service-tile group bg-gradient-to-br ${s.bg}`}>
                <div className="rtl-service-icon" style={{ color: s.color }}>
                  <s.icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <span className="text-[10px] font-semibold text-slate-600 group-hover:text-slate-800 transition-colors leading-tight text-center mt-1.5">{s.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ====== PERFORMANCE METRICS ====== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-fade-in" style={{ animationDelay: '0.12s', opacity: 0 }}>
        {loading ? [1,2,3,4].map(i => <Skeleton key={i} className="h-[100px] rounded-xl" />) : [
          {
            title: "Today's Volume", value: `₹${todayVol.toLocaleString('en-IN')}`,
            sub: `${todayCount} transactions`, icon: BarChart3,
            accent: '#4680ff', accentBg: 'bg-blue-500/8', trend: todayVol > 0 ? 'up' : null,
          },
          {
            title: "Today's Earning", value: `₹${todayComm.toFixed(2)}`,
            sub: `@ ${commRate}% rate`, icon: IndianRupee,
            accent: '#059669', accentBg: 'bg-emerald-500/8', trend: todayComm > 0 ? 'up' : null,
          },
          {
            title: 'Success Rate', value: `${successRate}%`,
            sub: `${stats?.today_success || 0} successful`, icon: Activity,
            accent: '#7c3aed', accentBg: 'bg-violet-500/8', trend: successRate >= 90 ? 'up' : successRate > 0 ? 'down' : null,
          },
          {
            title: 'Pending', value: pendingCount.toString(),
            sub: 'in progress', icon: RefreshCw,
            accent: '#d97706', accentBg: 'bg-amber-500/8', trend: null,
          },
        ].map((card, i) => (
          <div key={i} className="rtl-metric-card" data-testid={`metric-${i}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${card.accentBg}`}>
                <card.icon className="w-[18px] h-[18px]" style={{ color: card.accent }} strokeWidth={1.8} />
              </div>
              {card.trend && (
                <div className={`flex items-center gap-0.5 text-[10px] font-bold ${card.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                  {card.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                </div>
              )}
            </div>
            <p className="text-xl font-bold text-slate-900 font-mono tracking-tight">{card.value}</p>
            <p className="text-[10px] text-slate-400 mt-1 font-medium">{card.sub}</p>
            <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-b-xl" style={{ background: card.accent, opacity: 0.15 }} />
            <p className="absolute top-3 right-3 text-[9px] font-bold text-slate-300 uppercase tracking-wider">{card.title}</p>
          </div>
        ))}
      </div>

      {/* ====== CHART + SIDEBAR ====== */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Area Chart */}
        <Card className="lg:col-span-3 border border-slate-100 shadow-sm rounded-xl animate-fade-in" style={{ animationDelay: '0.18s', opacity: 0 }}>
          <CardHeader className="pb-1 pt-5 px-5">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-800">Weekly Volume</CardTitle>
                <p className="text-[11px] text-slate-400 mt-0.5">7-day recharge trend</p>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[#4680ff]" />
                <span className="text-[10px] text-slate-400 font-medium">Volume</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {loading ? <Skeleton className="w-full h-52" /> : chartData.length > 0 ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4680ff" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#4680ff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('en-IN', { weekday: 'short' })} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={42} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="volume" stroke="#4680ff" strokeWidth={2.5} fill="url(#areaFill)" dot={{ r: 3, fill: '#4680ff', strokeWidth: 0 }} activeDot={{ r: 5, fill: '#4680ff', stroke: '#fff', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-52 flex flex-col items-center justify-center text-slate-300">
                <BarChart3 className="w-10 h-10 mb-2 opacity-20" />
                <p className="text-xs text-slate-400">No data yet. Start recharging!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Column */}
        <div className="lg:col-span-2 space-y-4">
          {/* Lifetime Stats */}
          <div className="rtl-lifetime-card animate-fade-in" style={{ animationDelay: '0.22s', opacity: 0 }} data-testid="lifetime-stats">
            <h3 className="text-[10px] font-bold text-white/30 uppercase tracking-[0.12em] mb-3">Lifetime Performance</h3>
            {loading ? <Skeleton className="h-16 w-full bg-white/10" /> : (
              <div className="grid grid-cols-2 gap-3">
                <div className="rtl-lifetime-stat">
                  <Receipt className="w-4 h-4 text-blue-400 mb-1.5" />
                  <p className="text-lg font-bold text-white font-mono">{totalTxns.toLocaleString('en-IN')}</p>
                  <p className="text-[9px] text-white/30 font-semibold uppercase tracking-wider">Total Txns</p>
                </div>
                <div className="rtl-lifetime-stat">
                  <IndianRupee className="w-4 h-4 text-emerald-400 mb-1.5" />
                  <p className="text-lg font-bold text-white font-mono">&#8377;{totalEarned.toFixed(0)}</p>
                  <p className="text-[9px] text-white/30 font-semibold uppercase tracking-wider">Total Earned</p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <Card className="border border-slate-100 shadow-sm rounded-xl animate-fade-in" style={{ animationDelay: '0.26s', opacity: 0 }}>
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold text-slate-800">Recent Activity</CardTitle>
                <Link to="/history">
                  <Button variant="ghost" size="sm" className="text-[11px] text-[#4680ff] h-6 px-2 hover:bg-blue-50">
                    See All <ArrowRight className="w-3 h-3 ml-0.5" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              {loading ? <Skeleton className="h-44 w-full" /> : recentList.length === 0 ? (
                <div className="text-center py-10">
                  <Smartphone className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                  <p className="text-xs text-slate-400">No recharges yet</p>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {recentList.slice(0, 5).map((r, i) => {
                    const sc = statusConfig[r.status] || statusConfig.failed;
                    return (
                      <div key={r.id || i} className="rtl-activity-row group" data-testid={`recent-txn-${i}`}>
                        {/* Timeline dot */}
                        <div className="flex flex-col items-center mr-3">
                          <div className={`w-2.5 h-2.5 rounded-full ${sc.dot} ring-4 ring-white`} />
                          {i < recentList.slice(0, 5).length - 1 && <div className="w-px h-full bg-slate-100 mt-1" />}
                        </div>
                        <div className="flex items-center justify-between flex-1 min-w-0 py-2">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-700 font-mono truncate">{r.mobile}</p>
                            <p className="text-[10px] text-slate-400 capitalize">{r.operator} &middot; {r.recharge_type || 'prepaid'}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-xs font-bold text-slate-800 font-mono">&#8377;{r.amount}</p>
                            <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>{sc.label}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ====== QUICK RECHARGE CTA ====== */}
      <div className="rtl-cta-banner animate-fade-in" style={{ animationDelay: '0.3s', opacity: 0 }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-base font-bold text-slate-800">Start Recharging Now</p>
              <p className="text-xs text-slate-500 mt-0.5">Earn &#8377;{(commRate * 2.99).toFixed(2)} commission on every &#8377;299 recharge</p>
            </div>
          </div>
          <Link to="/recharge">
            <Button className="rtl-cta-btn" data-testid="cta-recharge-btn">
              <Zap className="w-4 h-4 mr-2" />Quick Recharge <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN DASHBOARD WRAPPER =====
const Dashboard = () => {
  const { user, updateUser } = useAuth();

  return (
    <DashboardLayout>
      <div data-testid="dashboard-container">
        {user?.role === 'retailer' ? (
          <RetailerDashboard user={user} />
        ) : (
          <AdminDashboard user={user} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
